import { EVENTS, FUNNEL_SECTIONS, SCROLL_MILESTONES } from './events';
import { trackEvent } from './gtag';

const SCROLL_THROTTLE_MS = 250;

let currentSection = 'home';
let previousSection = null;
let pendingEntryMethod = 'scroll';
let sectionEngagementStart = Date.now();
let sessionStart = Date.now();
let maxScrollDepth = 0;
let sectionsVisited = [];
let funnelStepsReached = new Set();
let reachedContact = false;
let scrollTimer = null;
let scrollMilestonesFired = new Set();
let exitTracked = false;
let initialized = false;

function getFunnelIndex(sectionId) {
  return FUNNEL_SECTIONS.indexOf(sectionId);
}

function trackFunnelProgress(sectionId) {
  const stepIndex = getFunnelIndex(sectionId);
  if (stepIndex < 0 || funnelStepsReached.has(sectionId)) {
    return;
  }

  funnelStepsReached.add(sectionId);
  const priorSteps = FUNNEL_SECTIONS.slice(0, stepIndex);

  trackEvent(EVENTS.FUNNEL_STEP, {
    step: sectionId,
    step_index: stepIndex + 1,
    prior_steps: priorSteps.join(','),
  });

  if (sectionId === 'contact' && !reachedContact) {
    reachedContact = true;
    trackEvent(EVENTS.FUNNEL_COMPLETE, {
      time_to_complete_seconds: Math.round((Date.now() - sessionStart) / 1000),
    });
  }
}

function endSectionEngagement(sectionId) {
  if (!sectionId) return;

  const durationSeconds = Math.round((Date.now() - sectionEngagementStart) / 1000);
  trackEvent(EVENTS.SECTION_ENGAGEMENT_END, {
    section_id: sectionId,
    duration_seconds: durationSeconds,
  });
}

function beginSectionEngagement(sectionId) {
  sectionEngagementStart = Date.now();
  trackEvent(EVENTS.SECTION_ENGAGEMENT_START, {
    section_id: sectionId,
  });
}

function recordSectionView(sectionId, entryMethod) {
  if (!FUNNEL_SECTIONS.includes(sectionId)) {
    return;
  }

  if (!sectionsVisited.includes(sectionId)) {
    sectionsVisited.push(sectionId);
  }

  const sectionIndex = getFunnelIndex(sectionId) + 1;

  trackEvent(EVENTS.SECTION_VIEW, {
    section_id: sectionId,
    section_index: sectionIndex,
    entry_method: entryMethod,
    previous_section: previousSection,
  });

  trackFunnelProgress(sectionId);
}

export function setPendingEntryMethod(entryMethod) {
  pendingEntryMethod = entryMethod;
}

export function notifySectionChange(sectionId) {
  if (!FUNNEL_SECTIONS.includes(sectionId) || sectionId === currentSection) {
    return;
  }

  const entryMethod = pendingEntryMethod;
  pendingEntryMethod = 'scroll';

  endSectionEngagement(currentSection);
  previousSection = currentSection;
  currentSection = sectionId;
  beginSectionEngagement(sectionId);
  recordSectionView(sectionId, entryMethod);
}

export function getSessionState() {
  const maxSectionIndex = sectionsVisited.reduce((max, sectionId) => {
    const index = getFunnelIndex(sectionId);
    return index > max ? index : max;
  }, -1);

  return {
    currentSection,
    previousSection,
    sectionsVisited: [...sectionsVisited],
    maxSection: maxSectionIndex >= 0 ? FUNNEL_SECTIONS[maxSectionIndex] : currentSection,
    maxScrollDepth,
    reachedContact,
    timeOnSiteSeconds: Math.round((Date.now() - sessionStart) / 1000),
  };
}

function getScrollDepthPercent() {
  const scrollableHeight =
    document.documentElement.scrollHeight - window.innerHeight;

  if (scrollableHeight <= 0) {
    return 100;
  }

  return Math.min(
    100,
    Math.round((window.scrollY / scrollableHeight) * 100)
  );
}

function handleScroll() {
  if (scrollTimer) return;

  scrollTimer = window.setTimeout(() => {
    scrollTimer = null;

    const depthPercent = getScrollDepthPercent();
    if (depthPercent > maxScrollDepth) {
      maxScrollDepth = depthPercent;
    }

    SCROLL_MILESTONES.forEach((milestone) => {
      if (depthPercent >= milestone && !scrollMilestonesFired.has(milestone)) {
        scrollMilestonesFired.add(milestone);
        trackEvent(EVENTS.SCROLL_DEPTH, {
          depth_percent: milestone,
          current_section: currentSection,
          document_height: document.documentElement.scrollHeight,
        });
      }
    });
  }, SCROLL_THROTTLE_MS);
}

function trackSessionExit() {
  if (exitTracked) return;
  exitTracked = true;

  endSectionEngagement(currentSection);

  const state = getSessionState();

  trackEvent(EVENTS.SESSION_EXIT, {
    last_section: state.currentSection,
    max_section: state.maxSection,
    max_scroll_depth: state.maxScrollDepth,
    sections_visited: state.sectionsVisited.join(','),
    time_on_site_seconds: state.timeOnSiteSeconds,
  });

  if (!state.reachedContact) {
    trackEvent(EVENTS.ABANDON_BEFORE_CONTACT, {
      last_section: state.currentSection,
      max_section: state.maxSection,
      max_scroll_depth: state.maxScrollDepth,
    });
  }
}

function handlePageHide() {
  trackSessionExit();
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    trackSessionExit();
  }
}

export function initSessionTracker() {
  if (initialized) return () => {};
  initialized = true;

  sessionStart = Date.now();
  sectionEngagementStart = Date.now();
  sectionsVisited = [currentSection];

  beginSectionEngagement(currentSection);
  recordSectionView(currentSection, 'initial_load');

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('pagehide', handlePageHide);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  handleScroll();

  return () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    if (scrollTimer) {
      clearTimeout(scrollTimer);
    }
    initialized = false;
  };
}

export function destroySessionTracker() {
  trackSessionExit();
}
